/**
 * LoginModal – overlay with Login component; closes on backdrop click or onLoginSuccess.
 */
import Login from './Login.jsx'
import Register from './Register.jsx'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../design-system/primitives/index.js'
import './LoginModal.css'

export default function LoginModal({
  isOpen,
  onClose,
  onLoginSuccess,
  onOpenPrivacy,
  initialMode = 'login',
  onboardingCopy = null,
  mobile = false,
}) {
  const [mode, setMode] = useState('login') // 'login' | 'register'

  useEffect(() => {
    if (!isOpen) return
    setMode(initialMode === 'register' ? 'register' : 'login')
  }, [isOpen, initialMode])

  const handleSuccess = () => {
    onLoginSuccess?.()
    onClose?.()
  }

  if (!isOpen) return null

  const hasOnboarding = Boolean(onboardingCopy)
  const hasSteps = Array.isArray(onboardingCopy?.steps) && onboardingCopy.steps.length > 0
  const featureChips = Array.isArray(onboardingCopy?.features) && onboardingCopy.features.length > 0
    ? onboardingCopy.features
    : [
        { label: 'Recipes', chipClass: 'step-chip--recipes' },
        { label: 'Pantry', chipClass: 'step-chip--pantry' },
        { label: 'Shop', chipClass: 'step-chip--shop' },
        { label: 'Plan', chipClass: 'step-chip--plan' },
      ]
  const canDismiss = true

  function renderOnboardingStep(step) {
    if (typeof step === 'string') return step
    if (step?.title && step?.description) {
      return (
        <>
          <span className="auth-modal-step-title">{step.title}</span>
          <span className="auth-modal-step-desc">{step.description}</span>
        </>
      )
    }
    return step?.description || step?.title || ''
  }

  const modal = (
    <div
      className={[
        'auth-modal-backdrop',
        hasOnboarding ? 'auth-modal-backdrop--onboarding' : '',
        mobile ? 'auth-modal-backdrop--mobile-sheet' : '',
      ].filter(Boolean).join(' ')}
      onClick={canDismiss ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'register' ? 'Create account' : 'Log in'}
    >
      <div
        className={[
          'auth-modal',
          hasOnboarding ? 'auth-modal-onboarding' : '',
          mobile ? 'auth-modal--mobile-sheet' : '',
        ].filter(Boolean).join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {hasOnboarding ? (
          <>
            <header className="auth-modal-onboarding-header">
              <div className="auth-modal-onboarding-title">
                <img src="/logo.svg" alt="" className="auth-modal-brand-logo" width={24} height={24} />
                <span>{onboardingCopy?.modalTitle || 'Set up my kitchen'}</span>
              </div>
              <Button type="button" className="auth-modal-close" onClick={onClose} aria-label="Close">
                ×
              </Button>
            </header>
            <div className="auth-modal-onboarding-body">
              <div className="auth-modal-onboarding-left">
                {onboardingCopy?.eyebrow ? (
                  <p className="auth-modal-eyebrow">{onboardingCopy.eyebrow}</p>
                ) : null}
                {onboardingCopy?.title ? (
                  <h2 className="auth-modal-title">{onboardingCopy.title}</h2>
                ) : null}
                {(onboardingCopy?.lead || []).map((paragraph, i) => (
                  <p key={i} className="auth-modal-lead">{paragraph}</p>
                ))}
                <div className="auth-modal-chips" aria-hidden="true">
                  {featureChips.map((feature) => (
                    <span
                      key={feature.label}
                      className={`step-chip${feature.chipClass ? ` ${feature.chipClass}` : ''}`}
                    >
                      {feature.label}
                    </span>
                  ))}
                </div>
                {hasSteps ? (
                  <section className="auth-modal-panel auth-modal-panel--features">
                    {onboardingCopy?.stepsHeading ? (
                      <h3 className="auth-modal-panel-title">{onboardingCopy.stepsHeading}</h3>
                    ) : null}
                    <ul className="auth-modal-list auth-modal-feature-steps">
                      {onboardingCopy.steps.map((step, i) => (
                        <li key={i}>{renderOnboardingStep(step)}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {Array.isArray(onboardingCopy?.benefits) && onboardingCopy.benefits.length > 0 ? (
                  <section className="auth-modal-panel">
                    {onboardingCopy?.benefitsHeading ? (
                      <h3 className="auth-modal-panel-title">{onboardingCopy.benefitsHeading}</h3>
                    ) : null}
                    <ul className="auth-modal-list">
                      {onboardingCopy.benefits.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {onboardingCopy?.trustLine ? (
                  <p className="auth-modal-trust">{onboardingCopy.trustLine}</p>
                ) : null}
              </div>
              <div className="auth-modal-onboarding-right">
                {mode === 'register' ? (
                  <div className="auth-modal-form-panel">
                    <h2 className="auth-modal-form-title">Create account</h2>
                    <Register
                      onRegisterSuccess={handleSuccess}
                      onSwitchToLogin={() => setMode('login')}
                      embedded
                      showTitle={false}
                      submitLabel={onboardingCopy?.ctaPrimary || 'Create account'}
                    />
                    <p className="auth-modal-legal">
                      By continuing, you agree to Terms and{' '}
                      {onOpenPrivacy ? (
                        <Button
                          type="button"
                          variant="link"
                          className="auth-modal-legal-link"
                          onClick={onOpenPrivacy}
                        >
                          Privacy Policy
                        </Button>
                      ) : (
                        <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </a>
                      )}
                      .
                    </p>
                  </div>
                ) : (
                  <Login onLoginSuccess={handleSuccess} onSwitchToRegister={() => setMode('register')} />
                )}
                {Array.isArray(onboardingCopy?.benefits) && onboardingCopy.benefits.length > 0 ? (
                  <section className="auth-modal-mobile-benefits" aria-label={onboardingCopy?.benefitsHeading || 'Benefits'}>
                    {onboardingCopy?.benefitsHeading ? (
                      <h3 className="auth-modal-panel-title">{onboardingCopy.benefitsHeading}</h3>
                    ) : null}
                    <ul className="auth-modal-list">
                      {onboardingCopy.benefits.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </div>
            </div>
          </>
        ) : mode === 'register' ? (
          <Register onRegisterSuccess={handleSuccess} onSwitchToLogin={() => setMode('login')} />
        ) : (
          <Login onLoginSuccess={handleSuccess} onSwitchToRegister={() => setMode('register')} />
        )}
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal

  return createPortal(modal, document.body)
}
